import { AIExpert } from '@garbera/shared';

export interface AISelectorProps {
  selectedExpert: AIExpert | null;
  isLoading?: boolean;
  disabled?: boolean;
}

export interface ExpertOption {
  id: number;
  name: string;
  description: string;
  specialization: string;
  avatar?: string;
  isAvailable: boolean;
}

export interface ExpertSwitchConfirmation {
  currentExpert: AIExpert;
  newExpert: AIExpert;
  hasActiveSession: boolean;
  messageCount: number;
}

export interface AIResponseStream {
  sessionId: number;
  messageId: number;
  content: string;
  isComplete: boolean;
  error?: string;
}

export interface MockAIResponse {
  content: string;
  delay: number;
  streamChunks?: string[];
}

export interface ChatContextProps {
  sessionId: number | null;
  selectedExpert: AIExpert | null;
  isConnected: boolean;
  isTyping: boolean;
  error: string | null;
}

// Default expert AI definitions for the system
export const DEFAULT_EXPERTS: Omit<AIExpert, 'id' | 'createdBy' | 'createdAt' | 'updatedAt'>[] = [
  {
    name: '不動産営業AI',
    description: '不動産営業のプロフェッショナルAI。顧客対応、価格交渉、物件提案に特化',
    customInstructions: 'あなたは経験豊富な不動産営業担当者です。顧客のニーズを理解し、最適な物件提案を行います。',
    isActive: true,
  },
  {
    name: '法務AI',
    description: '不動産法務の専門AI。契約書審査、法的リスク分析、重要事項説明をサポート',
    customInstructions: 'あなたは不動産法務の専門家です。法的観点から正確で安全なアドバイスを提供します。',
    isActive: true,
  },
  {
    name: '税務AI',
    description: '不動産税務の専門AI。税制優遇、節税対策、税務申告をサポート',
    customInstructions: 'あなたは不動産税務の専門家です。税制を活用した最適な提案を行います。',
    isActive: true,
  },
] as const;

export const EXPERT_AVATARS = {
  real_estate_sales: '🏠',
  legal: '⚖️',
  tax: '📊',
  default: '🤖',
} as const;

// 選択可能なアイコン絵文字のリスト
export const AVAILABLE_AVATARS = [
  { emoji: '🤖', label: 'ロボット', category: 'default' },
  { emoji: '🏠', label: '家', category: 'real_estate' },
  { emoji: '🏢', label: 'オフィスビル', category: 'real_estate' },
  { emoji: '🏘️', label: '住宅街', category: 'real_estate' },
  { emoji: '⚖️', label: '天秤', category: 'legal' },
  { emoji: '📊', label: 'グラフ', category: 'business' },
  { emoji: '💼', label: 'ブリーフケース', category: 'business' },
  { emoji: '👨‍💼', label: 'ビジネスマン', category: 'business' },
  { emoji: '👩‍💼', label: 'ビジネスウーマン', category: 'business' },
  { emoji: '📋', label: 'クリップボード', category: 'business' },
  { emoji: '📈', label: '上昇グラフ', category: 'business' },
  { emoji: '💰', label: 'お金', category: 'finance' },
  { emoji: '🏦', label: '銀行', category: 'finance' },
  { emoji: '📞', label: '電話', category: 'support' },
  { emoji: '💬', label: 'チャット', category: 'support' },
  { emoji: '🎓', label: '卒業帽', category: 'education' },
  { emoji: '📚', label: '本', category: 'education' },
  { emoji: '🎯', label: 'ダーツ', category: 'strategy' },
  { emoji: '🔍', label: '虫眼鏡', category: 'analysis' },
  { emoji: '✨', label: 'キラキラ', category: 'general' },
  { emoji: '💡', label: '電球', category: 'general' },
] as const;

export const MOCK_RESPONSES: Record<string, MockAIResponse> = {
  greeting: {
    content: 'こんにちは！不動産に関するご質問がございましたら、お気軽にお聞かせください。',
    delay: 1000,
  },
  general: {
    content: '申し訳ございませんが、より具体的な質問をいただけますでしょうか。不動産に関することでしたら、どのようなことでもお答えいたします。',
    delay: 1500,
  },
  legal: {
    content: '法的な観点から申し上げますと、契約書の内容を詳しく確認する必要があります。重要事項説明書も併せてご確認いただくことをお勧めします。',
    delay: 2000,
  },
} as const;